import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from '@utils/hooks';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  LegacyCampaignFields,
  getCalculatedFields,
  getLegacyCampaignFields,
} from '../dashboardSlice/campaign';
import {
  CampaignParams,
  useGetCampaignQuery,
} from '../dashboardSlice/campaignsApi';
import { campaignFromPayload } from '../dashboardSlice/responseMappers/campaign';
import { CampaignSchema } from '../dashboardSlice/schemas/campaign';
import { ReportType } from './useCurrentReportType';

export type CampaignWithCalculatedFields = CampaignSchema &
  ReturnType<typeof getCalculatedFields>;

type CampaignHook =
  | {
      status: 'loading';
      campaign: null;
      legacyCampaignFields: null;
      errorStatus: null;
    }
  | {
      status: 'error';
      errorStatus: number;
      campaign: null;
      legacyCampaignFields: null;
    }
  | {
      status: 'done';
      errorStatus: null;
      campaign: CampaignWithCalculatedFields;
      legacyCampaignFields: LegacyCampaignFields;
    };

export const useCampaignParameters = (
  campaignId: number | string | undefined
): CampaignParams => {
  const [searchParams] = useSearchParams();
  const channelPartner = searchParams.get('channel_partner') ?? undefined;
  const reportGroup = searchParams.get('report_group') ?? undefined;
  const type = (searchParams.get('type') as ReportType | null) ?? undefined;
  const reportId = searchParams.get('report_id') ?? undefined;
  const includeUnapproved = searchParams.get('includeUnapproved') ?? undefined;
  const showLci3Reports = searchParams.get('showLci3Reports') ?? undefined;

  const campaignParams: CampaignParams = useMemo(
    () => ({
      id: campaignId ? Number(campaignId) : 0,
      channel_partner: channelPartner,
      report_group: reportGroup,
      type,
      report_id: reportId,
      includeUnapproved: includeUnapproved === 'true' || undefined,
      showLci3Reports: showLci3Reports === 'true' || undefined,
    }),
    [
      campaignId,
      channelPartner,
      reportGroup,
      type,
      reportId,
      includeUnapproved,
      showLci3Reports,
    ]
  );

  return campaignParams;
};

const useCampaign = (campaignId: number | undefined): CampaignHook => {
  const sessionBudget = useAppSelector(
    state => state.dashboardSlice.ui.sessionBudget
  );
  const campaignParams = useCampaignParameters(campaignId);
  const {
    data: campaignPayload,
    isLoading,
    error,
  } = useGetCampaignQuery(campaignId ? campaignParams : skipToken);

  const nullResponse = {
    campaign: null,
    legacyCampaignFields: null,
    errorStatus: null,
  };

  // Go through this train of useMemo calls to maintain referential stability on the returned
  // `campaign` object, allowing for use in dependency arrays of other hooks
  const campaign = useMemo(
    () => (campaignPayload ? campaignFromPayload(campaignPayload) : undefined),
    [campaignPayload]
  );
  const calculatedFields = useMemo(() => {
    if (!campaign || !campaignId) return undefined;

    return getCalculatedFields(campaign, sessionBudget[campaignId]);
  }, [campaign, campaignId, sessionBudget]);

  const combinedCampaign = useMemo(() => {
    if (!campaign || !calculatedFields) return undefined;

    return { ...campaign, ...calculatedFields };
  }, [calculatedFields, campaign]);

  if (isLoading) {
    return {
      status: 'loading',
      ...nullResponse,
    };
  }

  if (
    error ||
    !campaignPayload ||
    !campaignId ||
    !campaign ||
    !calculatedFields ||
    !combinedCampaign
  ) {
    return {
      status: 'error',
      ...nullResponse,
      errorStatus:
        typeof error === 'object' &&
        'status' in error &&
        typeof error.status === 'number'
          ? error.status
          : 0,
    };
  }
  const legacyCampaignFields = getLegacyCampaignFields(
    campaignPayload,
    campaign,
    campaignParams.type
  );

  return {
    status: 'done',
    errorStatus: null,
    campaign: combinedCampaign,
    legacyCampaignFields,
  };
};

/**
 * Returns data for the current campaign based on the id parameter in the route.
 * If an id is not provided, an error will be thrown.
 */
export const useCurrentCampaign = (): CampaignHook => {
  const { id: currentCampaignId } = useParams<{ id: string }>();

  if (!currentCampaignId) {
    throw new Error('No campaign ID found for current page');
  }

  const parsedId = Number.parseInt(currentCampaignId);

  return useCampaign(parsedId);
};
